import gql from 'graphql-tag';
import apolloClient from '../apollo';
import Vue from 'vue';

/**
 * State
 */
const defaultEmployeeRequest = () => {
  return {
    _id: '_new',
    number: '_new',
    user: null,
    startTime: null,
    stopTime: null,
    description: '',
    employeeRequestState: 'UNAUDITED',
    employeeRequestType: '',
  };
};

const getDefaultState = () => {
  return {
    employeeRequests: [],
    employeeRequestCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an employeeRequest
 * used for all queries/mutations which return an employeeRequest
 * @type {DocumentNode}
 */
const EMPLOYEEREQUESTS_REQUEST_FIELDS = gql`
  fragment employeeRequestFields on EmployeeRequest {
    id
    number
    user {
      id
      firstname
      lastname
    }
    startTime
    stopTime
    description
    employeeRequestState
    employeeRequestType
    createdAt
    updatedAt
  }
`;

const EMPLOYEEREQUEST_REQUEST_FIELDS = gql`
  fragment employeeRequestFields on EmployeeRequest {
    id
    number
    user {
      id
      firstname
      lastname
    }
    startTime
    stopTime
    description
    employeeRequestState
    employeeRequestType
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} employeerequest
 */
const createPayload = (employeerequest) => {
  const payload = {
    ...(employeerequest.user ? { user: employeerequest.user.id } : {}),
    ...(employeerequest.description ? { description: employeerequest.description } : {}),
    ...(employeerequest.startTime ? { startTime: employeerequest.startTime } : {}),
    ...(employeerequest.stopTime ? { stopTime: employeerequest.stopTime } : {}),
    ...(employeerequest.employeeRequestState
      ? { employeeRequestState: employeerequest.employeeRequestState }
      : {}),
    ...(employeerequest.employeeRequestType
      ? { employeeRequestType: employeerequest.employeeRequestType }
      : {}),
  };
  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered employeeRequest list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchEmployeeRequestsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query employeeRequestsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: EmployeeRequestFilterInput
          ) {
            employeeRequestsPaginated(
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            ) {
              employeeRequests {
                ...employeeRequestFields
              }
              employeeRequestCount
            }
          }
          ${EMPLOYEEREQUESTS_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });

      commit('SET_EMPLOYEEREQUESTS', response.data.employeeRequestsPaginated.employeeRequests);
      commit(
        'SET_EMPLOYEEREQUEST_COUNT',
        response.data.employeeRequestsPaginated.employeeRequestCount
      );
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchEmployeeRequestByNumber({ commit, dispatch }, employeeRequestNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query employeeRequestByNumber($employeeRequestNumber: String!) {
            employeeRequestByNumber(employeeRequestNumber: $employeeRequestNumber) {
              ...employeeRequestFields
            }
          }
          ${EMPLOYEEREQUEST_REQUEST_FIELDS}
        `,
        variables: {
          employeeRequestNumber: employeeRequestNumber.toString(),
        },
      });
      commit('ADD_OR_UPDATE_EMPLOYEEREQUEST', response.data.employeeRequestByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * Update employee request verification state
   * @param {*} param0
   * @param {*} param1
   */
  async updateEmployeeRequestVerificationState(
    { commit, dispatch },
    { employeeRequestNumber, verificationStateInput }
  ) {
    try {
      // Convert to integer since the schema expects Int!
      const numericEmployeeRequestNumber = parseInt(employeeRequestNumber, 10);

      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateEmployeeRequestVerificationState(
            $employeeRequestNumber: Int!
            $verificationStateInput: VerificationStateInput!
          ) {
            updateEmployeeRequestVerificationState(
              employeeRequestNumber: $employeeRequestNumber
              verificationStateInput: $verificationStateInput
            )
          }
        `,
        variables: {
          employeeRequestNumber: numericEmployeeRequestNumber,
          verificationStateInput: verificationStateInput,
        },
      });

      if (response.data && response.data.updateEmployeeRequestVerificationState) {
        dispatch('message', {
          message: 'Status aktualisiert!',
          type: 'success',
          lifetime: 2,
        });

        // Update the employee request with the new state
        commit('UPDATE_EMPLOYEEREQUEST_VERIFICATION_STATE', {
          employeeRequestNumber,
          verificationStateType: verificationStateInput.verificationStateType,
          verificationState: response.data.updateEmployeeRequestVerificationState,
        });

        return response.data.updateEmployeeRequestVerificationState;
      } else {
        throw new Error('No response data from update verification state mutation');
      }
    } catch (err) {
      console.error('Update verification state error:', err);
      dispatch('message', {
        message: 'Fehler beim Aktualisieren des Status!',
        type: 'danger',
        lifetime: 1,
      });
      throw err;
    }
  },
  async createEmployeeRequest({ commit, dispatch }, employeerequest) {
    const payload = createPayload(employeerequest);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createEmployeeRequest($payload: EmployeeRequestInput!) {
            createEmployeeRequest(employeeRequestInput: $payload) {
              ...employeeRequestFields
            }
          }
          ${EMPLOYEEREQUEST_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Mitarbeiteranfrage gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_EMPLOYEEREQUEST', '_new'); // remove temporary _new employeerequest
      commit('ADD_OR_UPDATE_EMPLOYEEREQUEST', response.data.createEmployeeRequest);
      return response.data.createEmployeeRequest.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateEmployeeRequest({ commit, dispatch }, employeerequest) {
    const payload = createPayload(employeerequest);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateEmployeeRequest(
            $employeeRequestNumber: String!
            $payload: EmployeeRequestInput!
          ) {
            updateEmployeeRequest(
              employeeRequestNumber: $employeeRequestNumber
              employeeRequestInput: $payload
            ) {
              ...employeeRequestFields
            }
          }
          ${EMPLOYEEREQUEST_REQUEST_FIELDS}
        `,
        variables: {
          employeeRequestNumber: employeerequest.number.toString(),
          payload: payload,
        },
      });

      if (response.data && response.data.updateEmployeeRequest) {
        dispatch('message', {
          message: 'Mitarbeiteranfrage aktualisiert!',
          type: 'success',
          lifetime: 2,
        });

        // Ensure we're properly handling the returned data
        const updatedRequest = response.data.updateEmployeeRequest;
        commit('ADD_OR_UPDATE_EMPLOYEEREQUEST', updatedRequest);
        return updatedRequest;
      } else {
        throw new Error('No response data from update mutation');
      }
    } catch (err) {
      console.error('Update error:', err);
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw err;
    }
  },
  async deleteEmployeeRequest({ commit, dispatch }, employeeRequestNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteEmployeeRequest($employeeRequestNumber: String!) {
            deleteEmployeeRequest(employeeRequestNumber: $employeeRequestNumber)
          }
        `,
        variables: {
          employeeRequestNumber: employeeRequestNumber.toString(),
        },
      });
      dispatch('message', {
        message: 'Mitarbeiteranfrage gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_EMPLOYEEREQUEST', employeeRequestNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new employeerequest object in store and return it
   * @param {*} param0
   */
  async initEmployeeRequest({ commit }) {
    commit('ADD_OR_UPDATE_EMPLOYEEREQUEST', defaultEmployeeRequest());
  },

  employeeRequestLocalUpdate({ commit }, employeerequest) {
    commit('ADD_OR_UPDATE_EMPLOYEEREQUEST', employeerequest);
  },

  employeeRequestCleanup({ commit }) {
    commit('REMOVE_EMPLOYEEREQUEST', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_EMPLOYEEREQUEST_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_EMPLOYEEREQUESTS: (state, employeeRequests) =>
    employeeRequests ? (state.employeeRequests = employeeRequests) : (state.employeeRequests = []),
  ADD_EMPLOYEEREQUEST: (state, employeerequest) => state.employeeRequests.push(employeerequest),
  ADD_OR_UPDATE_EMPLOYEEREQUEST: (state, updatedEmployeeRequest) => {
    const existingEmployeeRequest = state.employeeRequests.find(
      (employeerequest) => employeerequest._id === updatedEmployeeRequest._id
    );
    if (existingEmployeeRequest) {
      Object.assign(existingEmployeeRequest, updatedEmployeeRequest);
    } else {
      state.employeeRequests.push(updatedEmployeeRequest);
    }
  },
  REMOVE_EMPLOYEEREQUEST: (state, employeerequestId) => {
    const index = state.employeeRequests.findIndex(
      (employeerequest) => employeerequest._id === employeerequestId
    );
    state.employeeRequests.splice(index, 1);
  },
  UPDATE_EMPLOYEEREQUEST: (state, updatedEmployeeRequest) => {
    const employeerequest = state.employeeRequests.find(
      (employeerequest) => employeerequest._id === updatedEmployeeRequest._id
    );
    Object.assign(employeerequest, updatedEmployeeRequest);
  },
  // Add this to your mutations object
  UPDATE_EMPLOYEEREQUEST_VERIFICATION_STATE: (
    state,
    { employeeRequestNumber, verificationStateType, verificationState }
  ) => {
    const employeeRequest = state.employeeRequests.find(
      (request) => request.number.toString() === employeeRequestNumber.toString()
    );

    if (employeeRequest) {
      employeeRequest[verificationStateType] = verificationState;
    }
  },
  SET_EMPLOYEEREQUEST_COUNT: (state, employeeRequestCount) =>
    (state.employeeRequestCount = employeeRequestCount),
};

/**
 * Getters
 */
const getters = {
  getEmployeeRequests: (state) => state.employeeRequests,
  getEmployeeRequest: (state) => (employeerequestId) =>
    state.employeeRequests.find((employeerequest) => employeerequest._id == employeerequestId),
  getEmployeeRequestByNumber: (state) => (employeeRequestNumber) =>
    state.employeeRequests.find(
      (employeerequest) => employeerequest.number == employeeRequestNumber
    ),
  getEmployeeRequestByState: (state) => (state) =>
    state.employeeRequests.filter(
      (employeerequest) => employeerequest.employeeRequestState == state
    ),
  getEmployeeRequestByType: (state) => (type) =>
    state.employeeRequests.filter((employeerequest) => employeerequest.employeeRequestType == type),
  getEmployeeRequestCount: (state) => state.employeeRequestCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
